import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Icon,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import PropTypes from "prop-types";
import {useState} from "react";

const ESTADOS = Object.freeze({
    PENDENTE: {
        severity: "info",
        titulo: "Aguardando entrega ao fornecedor"
    },
    PROCESSAMENTO: {
        severity: "warning",
        titulo: "Entrega em processamento pelo conector"
    },
    ACEITA_PELO_FORNECEDOR: {
        severity: "success",
        titulo: "Entrega aceita pelo fornecedor"
    },
    REJEITADA_PELO_FORNECEDOR: {
        severity: "error",
        titulo: "Entrega rejeitada pelo fornecedor"
    },
    FALHA_ANTES_DO_ENVIO: {
        severity: "error",
        titulo: "Entrega não transmitida ao fornecedor"
    },
    RESULTADO_INDETERMINADO: {
        severity: "warning",
        titulo: "Resultado do fornecedor indeterminado"
    }
});

function FornecedorEntregaCard({
    ocorrencia,
    podeReconciliar,
    onReconciliar,
    loading
}) {
    const [justificativa, setJustificativa] = useState("");
    const [protocolo, setProtocolo] = useState("");
    const resposta = ocorrencia?.resposta || {};
    const estado = resposta.estadoEntregaFornecedor || "PENDENTE";
    const apresentacao = ESTADOS[estado] || ESTADOS.PENDENTE;
    const indeterminado = estado === "RESULTADO_INDETERMINADO";
    const justificativaValida = justificativa.trim().length >= 10;

    function reconciliar(acao) {
        onReconciliar({
            acao,
            justificativa: justificativa.trim(),
            protocoloFornecedor:
                acao === "CONFIRMAR_ACEITE" ? protocolo.trim() : undefined
        });
    }

    return (
        <Grid item xs={12}>
            <Card variant="outlined" sx={{borderRadius: 2}}>
                <CardContent>
                    <Stack spacing={2}>
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                        >
                            <Box
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "action.hover",
                                    color: "primary.main"
                                }}
                            >
                                <Icon>outbox</Icon>
                            </Box>
                            <Box>
                                <Typography variant="h6">
                                    Entrega ao fornecedor
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Protocolo e estado da etapa externa
                                    contratada.
                                </Typography>
                            </Box>
                        </Stack>

                        <Alert severity={apresentacao.severity}>
                            <strong>{apresentacao.titulo}.</strong> O aceite do
                            fornecedor não representa aceite ou processamento
                            pelo TCE-MS.
                        </Alert>

                        <Grid container spacing={2}>
                            <Resumo
                                label="Protocolo do fornecedor"
                                valor={resposta.protocoloFornecedor}
                            />
                            <Resumo
                                label="Aceite do TCE-MS"
                                valor={resposta.aceiteTceMs || "NÃO CONFIRMADO"}
                            />
                            <Resumo
                                label="Entrega"
                                valor={resposta.entregaFornecedorId}
                            />
                        </Grid>

                        {resposta.aceiteTceMs === "CONFIRMADO" && (
                            <Alert severity="success">
                                Aceite oficial do TCE-MS registrado:{" "}
                                {ocorrencia.codigoRegistroInformacao ||
                                    resposta.codigoRegistroInformacao}
                                .
                                {resposta.evidenciaTceMs && (
                                    <Typography variant="body2">
                                        Comprovante: {resposta.evidenciaTceMs}
                                    </Typography>
                                )}
                            </Alert>
                        )}
                        {estado === "ACEITA_PELO_FORNECEDOR" &&
                            resposta.aceiteTceMs !== "CONFIRMADO" &&
                            podeReconciliar && (
                                <ConfirmarAceiteTceForm
                                    resposta={resposta}
                                    onReconciliar={onReconciliar}
                                    loading={loading}
                                />
                            )}
                        {indeterminado && podeReconciliar && (
                            <Box
                                sx={{
                                    border: "1px solid",
                                    borderColor: "warning.light",
                                    borderRadius: 2,
                                    p: 2
                                }}
                            >
                                <Stack spacing={2}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                    >
                                        Reconciliação administrativa
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Confirme o resultado somente após
                                        consultar a evidência externa. Toda
                                        decisão fica auditada.
                                    </Typography>
                                    <TextField
                                        label="Justificativa da reconciliação"
                                        value={justificativa}
                                        onChange={(event) =>
                                            setJustificativa(event.target.value)
                                        }
                                        inputProps={{maxLength: 500}}
                                        helperText={`${
                                            justificativa.trim().length
                                        }/500 caracteres — mínimo 10`}
                                        multiline
                                        minRows={2}
                                        required
                                    />
                                    <TextField
                                        label="Protocolo para confirmação de aceite"
                                        value={protocolo}
                                        onChange={(event) =>
                                            setProtocolo(event.target.value)
                                        }
                                        inputProps={{maxLength: 200}}
                                        helperText="Obrigatório apenas para confirmar o aceite pelo fornecedor"
                                    />
                                    <Stack
                                        direction={{xs: "column", md: "row"}}
                                        spacing={1}
                                    >
                                        <Button
                                            variant="contained"
                                            color="success"
                                            disabled={
                                                loading ||
                                                !justificativaValida ||
                                                !protocolo.trim()
                                            }
                                            onClick={() =>
                                                reconciliar("CONFIRMAR_ACEITE")
                                            }
                                        >
                                            Confirmar aceite
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            disabled={
                                                loading || !justificativaValida
                                            }
                                            onClick={() =>
                                                reconciliar(
                                                    "CONFIRMAR_REJEICAO"
                                                )
                                            }
                                        >
                                            Confirmar rejeição
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            disabled={
                                                loading || !justificativaValida
                                            }
                                            onClick={() =>
                                                reconciliar(
                                                    "REENFILEIRAR_NAO_RECEBIDA"
                                                )
                                            }
                                        >
                                            Confirmar não recebimento e
                                            reenfileirar
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Box>
                        )}

                        {indeterminado && !podeReconciliar && (
                            <Alert severity="info">
                                A reconciliação exige o perfil master. Não crie
                                outra ocorrência nem reenvie automaticamente.
                            </Alert>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        </Grid>
    );
}

function Resumo({label, valor}) {
    return (
        <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body1" fontWeight={600}>
                {valor || "Não informado"}
            </Typography>
        </Grid>
    );
}

Resumo.propTypes = {
    label: PropTypes.string.isRequired,
    valor: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

FornecedorEntregaCard.propTypes = {
    ocorrencia: PropTypes.object,
    podeReconciliar: PropTypes.bool,
    onReconciliar: PropTypes.func.isRequired,
    loading: PropTypes.bool
};

export default FornecedorEntregaCard;

function ConfirmarAceiteTceForm({resposta, onReconciliar, loading}) {
    const [codigo, setCodigo] = useState("");
    const [evidencia, setEvidencia] = useState("");
    const [justificativa, setJustificativa] = useState("");
    return (
        <Stack
            spacing={2}
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2
            }}
        >
            <Typography variant="subtitle1" fontWeight={600}>
                Registrar aceite oficial do TCE-MS
            </Typography>
            <Alert severity="info">
                Após conferir o comprovante oficial deste pacote, registre o
                código do TCE-MS. O protocolo do fornecedor permanece separado.
                A operação reutiliza esta ocorrência e não transmite dados.
            </Alert>
            <TextField
                label="Código de registro do TCE-MS"
                value={codigo}
                inputProps={{maxLength: 100}}
                onChange={(e) => setCodigo(e.target.value)}
                required
            />
            <TextField
                label="Referência do comprovante oficial"
                value={evidencia}
                inputProps={{maxLength: 2000}}
                onChange={(e) => setEvidencia(e.target.value)}
                helperText="Identifique o documento ou a consulta oficial que comprova o aceite integral deste pacote (mínimo 10 caracteres)."
                required
            />
            <TextField
                label="Justificativa do aceite oficial"
                value={justificativa}
                inputProps={{maxLength: 500}}
                onChange={(e) => setJustificativa(e.target.value)}
                multiline
                minRows={2}
                required
            />
            <Button
                variant="contained"
                disabled={
                    loading ||
                    !codigo.trim() ||
                    evidencia.trim().length < 10 ||
                    justificativa.trim().length < 10 ||
                    !resposta.hashPayload ||
                    !resposta.protocoloFornecedor
                }
                onClick={() =>
                    onReconciliar({
                        acao: "CONFIRMAR_ACEITE",
                        justificativa: justificativa.trim(),
                        protocoloFornecedor: resposta.protocoloFornecedor,
                        codigoRegistroInformacao: codigo.trim(),
                        evidenciaTceMs: evidencia.trim(),
                        hashPayload: resposta.hashPayload
                    })
                }
            >
                Registrar comprovante do TCE-MS
            </Button>
        </Stack>
    );
}
